import React from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import { Code, EmptyState, LoadingOverlay, StatusChip } from 'dashboard/components/common';

const STATUS_COLORS = { out: 'error', low: 'warning', ok: 'success' };
const STATUS_LABEL  = { out: 'Out',   low: 'Low',     ok: 'OK' };

/**
 * Low-stock variants table with inline threshold editing.
 *
 * Each row's threshold is editable. `edits` is a parent-owned map
 * `{ variant_id: newThreshold }`; rows with a pending edit show a
 * green outline so the user can see exactly what's unsaved.
 */
export default function LowStockTable({ rows, loading, edits, onSetEdit }) {
  return (
    <LoadingOverlay loading={loading && rows.length === 0} minHeight={200}>
      <TableContainer
        component={Box}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', overflowX: 'auto' }}
      >
        <Table size='medium'>
          <TableHead sx={{ bgcolor: 'action.hover' }}>
            <TableRow>
              <TableCell>Product</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell align='right'>Available</TableCell>
              <TableCell align='right'>Threshold</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5}><EmptyState>Loading…</EmptyState></TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={5}><EmptyState>Nothing matches.</EmptyState></TableCell></TableRow>
            )}
            {rows.map((r) => {
              const editValue = edits[String(r.variant_id)];
              const display   = editValue == null ? r.threshold : editValue;
              const dirty     = editValue != null && editValue !== r.threshold;
              return (
                <TableRow key={r.variant_id}>
                  <TableCell>
                    <Typography fontWeight='600'>{r.product_name}</Typography>
                    {r.variant_name && (
                      <Typography variant='caption' color='text.secondary'>
                        {r.variant_name}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell><Code>{r.sku || '—'}</Code></TableCell>
                  <TableCell align='right'>
                    <Typography
                      fontWeight='700'
                      sx={{ color: r.available === 0 ? 'error.main' : 'text.primary' }}
                    >
                      {r.available}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>
                    <TextField
                      size='small' type='number'
                      value={display}
                      onChange={(e) => onSetEdit(String(r.variant_id), e.target.value)}
                      inputProps={{ min: 0 }}
                      sx={{
                        width: 90,
                        '& .MuiOutlinedInput-notchedOutline': dirty
                          ? { borderColor: 'success.main', borderWidth: 2 }
                          : undefined,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      status={r.status}
                      label={STATUS_LABEL[r.status] || r.status}
                      colors={STATUS_COLORS}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </LoadingOverlay>
  );
}
